import { useQuery, gql, NetworkStatus } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { subWeeks, format } from 'date-fns';
import { createAfterCursor } from '../../../utils/relay';
import { NotificationLevel } from '../../enums';
import { useUpdateSearchParams } from '../../table/hooks';

const NOTIFICATIONS_FRAGMENT = gql`
    fragment NotificationTypeFields on NotificationType {
        id
        swarmType
        createdAt
        level
        data
        isLong
        isLegacy
    }
`;

const NOTIFICATIONS = gql`
    query notifications(
        $orderBy: String
        $before: String
        $after: String
        $first: Int
        $last: Int
        $levels: [NotificationLevel]
        $search: String
        $createdAt_Gte: DateTime
        $createdAt_Lte: DateTime
        $mpPk: Int
    ) {
        notifications(
            orderBy: $orderBy
            before: $before
            after: $after
            first: $first
            last: $last
            levels: $levels
            search: $search
            createdAt_Gte: $createdAt_Gte
            createdAt_Lte: $createdAt_Lte
            mpPk: $mpPk
        ) {
            totalCount
            edges {
                node {
                    ...NotificationTypeFields
                }
            }
            pageInfo {
                startCursor
                endCursor
            }
        }
    }
    ${NOTIFICATIONS_FRAGMENT}
`;

function getKeysWithTrueValues(obj) {
    return Object.entries(obj)
        .filter(([_key, value]) => value)
        .map(([key]) => key);
}

function getLastWeekDateTimeLocal() {
    const now = new Date();
    const lastWeek = subWeeks(now, 1);
    return format(lastWeek, "yyyy-MM-dd'T'HH:mm");
}

export function useFiltrationControl() {
    const { searchParams, updateSearchParams } = useUpdateSearchParams();

    const [filters, setFilters] = useState(() => {
        const initialFilters = {
            levels: {},
            search: searchParams.get('search') || '',
            startDate: searchParams.get('startDate') || getLastWeekDateTimeLocal(),
            endDate: searchParams.get('endDate') || '',
            mpPk: searchParams.get('mpPk') || '',
        };

        const levels = searchParams.get('levels');
        if (levels) {
            levels.split(',').forEach((level) => {
                initialFilters.levels[level] = true;
            });
        } else {
            Object.values(NotificationLevel).forEach((level) => {
                initialFilters.levels[level] = true;
            });
        }

        return initialFilters;
    });

    useEffect(() => {
        updateSearchParams('levels', getKeysWithTrueValues(filters.levels));
    }, [filters.levels, updateSearchParams]);

    // Handles changes to filters, updating both the local state and search parameters.
    // Special handling for 'levels' is done in the 'useEffect' hook.
    const handleFilterChange = useCallback(
        (key, value, filterKey = null) => {
            setFilters((prevState) => {
                if (filterKey) {
                    return {
                        ...prevState,
                        [filterKey]: {
                            ...prevState[filterKey],
                            [key]: value,
                        },
                    };
                }
                return {
                    ...prevState,
                    [key]: value,
                };
            });
            if (filterKey !== 'levels') {
                updateSearchParams(key, value);
            }
        },
        [updateSearchParams]
    );

    return {
        filters,
        setLevelsFilter: (key, value) => handleFilterChange(key, value, 'levels'),
        setStartDateFilter: (value) => handleFilterChange('startDate', value),
        setEndDateFilter: (value) => handleFilterChange('endDate', value),
        setMeasuringPointFilter: (value) => handleFilterChange('mpPk', value),
        setSearchFilter: (value) => handleFilterChange('search', value),
    };
}

function createQueryVariables(
    { currentPage, pageSize, orderBy },
    { filters: { levels, search, startDate, endDate, mpPk } }
) {
    return {
        first: pageSize,
        after: createAfterCursor(currentPage, pageSize),
        ...(orderBy && { orderBy }),
        ...(levels && { levels: getKeysWithTrueValues(levels) }),
        ...(search && { search }),
        ...(startDate && {
            createdAt_Gte: new Date(startDate).toISOString(),
        }),
        ...(endDate && {
            createdAt_Lte: new Date(endDate).toISOString(),
        }),
        ...(mpPk && { mpPk: Number(mpPk) }),
    };
}

export function useNotificationQueryGenerator(orderAndPaginationControl, filtrationControl) {
    const [notifications, setNotifications] = useState([]);

    const queryVariables = useMemo(
        () => createQueryVariables(orderAndPaginationControl, filtrationControl),
        [orderAndPaginationControl, filtrationControl]
    );

    const { data, loading, networkStatus } = useQuery(NOTIFICATIONS, {
        variables: queryVariables,
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        if (data) {
            setNotifications(data.notifications?.edges ?? []);
            orderAndPaginationControl.setTotalCount(data.notifications?.totalCount);
        }
    }, [data, orderAndPaginationControl, filtrationControl]);

    return {
        notifications,
        loading: loading && networkStatus === NetworkStatus.loading,
        updating: loading && networkStatus === NetworkStatus.setVariables,
    };
}

export function useHover() {
    const [isHovering, setIsHovering] = useState(false);

    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);

    const hoverProps = {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
    };

    return [isHovering, hoverProps];
}
